import { ref, readonly } from 'vue';
import { useUniqueIds } from '@/composables/useUniqueIds';

interface ActionResultMessageColors {
  color: string;
  'border-color': string;
  'background-color': string;
}

interface ActionResultMessage {
  id: number;
  message: string;
  colors: ActionResultMessageColors;
  fadeOut: boolean;
}

const ALERT_DURATION = 5000;
const FADE_DURATION = 1000;

const ACTION_RESULT_MESSAGE_COLORS_GREEN: ActionResultMessageColors = {
  color: '#155724',
  'border-color': '#c3e6cb',
  'background-color': '#d4edda'
};
const ACTION_RESULT_MESSAGE_COLORS_RED: ActionResultMessageColors = {
  color: '#721c24',
  'border-color': '#f5c6cb',
  'background-color': '#f8d7da'
};
const ACTION_RESULT_MESSAGE_COLORS_BLUE: ActionResultMessageColors = {
  color: '#004085',
  'border-color': '#b8daff',
  'background-color': '#cce5ff'
};
const ACTION_RESULT_MESSAGE_COLORS_LIGHT_GREY: ActionResultMessageColors = {
  color: '#383d41',
  'border-color': '#d6d8db',
  'background-color': '#e2e3e5'
};
const ACTION_RESULT_MESSAGE_COLORS_YELLOW: ActionResultMessageColors = {
  color: '#856404',
  'border-color': '#ffeeba',
  'background-color': '#fff3cd'
};
const ACTION_RESULT_MESSAGE_COLORS_GREY: ActionResultMessageColors = {
  color: '#1b1e21',
  'border-color': '#c6c8ca',
  'background-color': '#d6d8d9'
};

const actionResultMessages = ref<ActionResultMessage[]>([]);

function removeActionResultMessage(actionResultMessageId: number) {
  actionResultMessages.value = actionResultMessages.value.filter(actionResultMessage => actionResultMessage.id !== actionResultMessageId);
}

function addActionResultMessage(message: string, colors: ActionResultMessageColors) {
  const { getPositiveUniqueId } = useUniqueIds();

  const id = getPositiveUniqueId();

  actionResultMessages.value.push({
    id,
    message,
    colors,
    fadeOut: false
  });

  window.setTimeout(() => {
    const desiredActionResultMessage = actionResultMessages.value.find(actionResultMessage => actionResultMessage.id === id);

    if (desiredActionResultMessage) {
      desiredActionResultMessage.fadeOut = true;
    }

    window.setTimeout(() => {
      removeActionResultMessage(id);
    }, FADE_DURATION);
  }, ALERT_DURATION - FADE_DURATION);
}

function addGreenActionResultMessage(message: string) {
  addActionResultMessage(message, ACTION_RESULT_MESSAGE_COLORS_GREEN);
}

function addRedActionResultMessage(message: string) {
  addActionResultMessage(message, ACTION_RESULT_MESSAGE_COLORS_RED);
}

function addBlueActionResultMessage(message: string) {
  addActionResultMessage(message, ACTION_RESULT_MESSAGE_COLORS_BLUE);
}

function addLightGreyActionResultMessage(message: string) {
  addActionResultMessage(message, ACTION_RESULT_MESSAGE_COLORS_LIGHT_GREY);
}

function addYellowActionResultMessage(message: string) {
  addActionResultMessage(message, ACTION_RESULT_MESSAGE_COLORS_YELLOW);
}

function addGreyActionResultMessage(message: string) {
  addActionResultMessage(message, ACTION_RESULT_MESSAGE_COLORS_GREY);
}

export function useActionResultMessages() {
  return {
    actionResultMessages: readonly(actionResultMessages),
    addGreenActionResultMessage,
    addRedActionResultMessage,
    addBlueActionResultMessage,
    addLightGreyActionResultMessage,
    addYellowActionResultMessage,
    addGreyActionResultMessage,
    removeActionResultMessage
  };
}
